/**
 * The empire's economy: distance, discontent and money.
 *
 * Three systems that all do the same job from different directions — they are what
 * stop "more cities, always" from being the only strategy. Without them a 4X is an
 * arithmetic exercise in expanding as fast as possible.
 *
 *   ADMINISTRATIVE DRAG makes empire SHAPE matter. Gold and production are lost in
 *   proportion to a city's distance from the seat of government, so a compact realm
 *   is worth more than a sprawling one of the same size, and where the palace stands
 *   is a real decision. This is the project's own take on corruption; a courthouse
 *   recovers part of it, and roads will recover more in Phase 5b.
 *
 *   CONTENTMENT makes city SIZE cost something. Citizens past a threshold are
 *   discontented unless a building or a garrison buys them back, and a city with more
 *   discontented than content citizens falls into disorder and produces nothing.
 *
 *   MAINTENANCE makes BUILDING cost something after it is built. Every building and
 *   unit draws gold every turn, so an empire can be over-built into insolvency.
 *
 * All three are data. None of the numbers below are in this file.
 */

import { traitSum } from '../civs/traits.js';
import type { Content } from '../../../content/schema/index.js';
import { wrappedDistance } from '../state/start.js';
import type { City, Civ, GameState, Id } from '../state/types.js';
import { cityIndex, cityYield, type Yield } from './index.js';
import { buildingsOf, effectTotal } from './production.js';
import { inDisorder, specialistOutput } from './mood.js';
import { civEffectTotal } from './wonders.js';
import { governmentEffects, maxScienceRate } from '../government/index.js';
import { unitsOf } from '../units/index.js';

// ─── the seat of government ────────────────────────────────────────────────────

export function isCapital(content: Content, city: City): boolean {
  return buildingsOf(content, city).some((b) => b.effects.isSeatOfGovernment === true);
}

/** A civilisation's capital, or null when it has none — a civ with no cities at all. */
export function capitalOf(state: GameState, content: Content, civId: Id): City | null {
  const owned = Object.values(state.cities)
    .filter((c) => c.owner === civId)
    .sort((a, b) => a.id - b.id);
  return owned.find((c) => isCapital(content, c)) ?? owned[0] ?? null;
}

// ─── administrative drag ───────────────────────────────────────────────────────

/**
 * The share of a city's gold and production that never reaches the empire, from 0 to
 * `drag.maxLoss`.
 *
 * Distance is measured to the capital the short way round the world, so a realm that
 * straddles the date line is not punished for the map's seam.
 */
export function dragFraction(state: GameState, content: Content, city: City): number {
  const rules = content.rules.drag;
  const capital = capitalOf(state, content, city.owner);
  if (!capital) return 0;
  if (capital.id === city.id) return 0;

  const distance = wrappedDistance(state.map, cityIndex(state.map, city), cityIndex(state.map, capital));
  const beyond = Math.max(0, distance - rules.capitalFreeRadius);

  // The government decides how much distance costs, or (communism) sets one loss for
  // every city however far it stands (ADR-0059).
  const government = governmentEffects(state, content, city.owner);
  let loss = Math.min(rules.maxLoss, beyond * rules.lossPerTile * government.dragMultiplier + government.dragFlat);

  // Courthouses and the like recover part of what distance would take, and so does a
  // road under the city itself: a building is the city organising itself, a road is
  // the empire reaching out to meet it.
  let recovered = 0;
  for (const building of buildingsOf(content, city)) recovered += building.effects.dragReduction ?? 0;
  const here = cityIndex(state.map, city);
  recovered += content.improvements.get(state.map.improvement[here]!).effects.dragReduction ?? 0;
  if (state.map.road[here] === 1) recovered += content.route?.effects.dragReduction ?? 0;

  loss *= Math.max(0, 1 - Math.min(1, recovered));

  return loss;
}

/** What a city actually contributes after distance has taken its share. */
export function effectiveYield(state: GameState, content: Content, city: City): Yield {
  const raw = cityYield(state, content, city);
  const loss = dragFraction(state, content, city);
  const keep = (value: number): number => Math.max(0, Math.floor(value * (1 - loss)));
  return {
    // Food is local — it is eaten where it is grown, so distance cannot take it.
    food: raw.food,
    production: inDisorder(state, content, city)
      ? Math.floor(keep(raw.production) * content.rules.contentment.disorderProductionMultiplier)
      : keep(raw.production),
    gold: keep(raw.gold),
  };
}

// ─── contentment ───────────────────────────────────────────────────────────────

// Mood lives in ./mood.ts so the tile governor can use it without a cycle; re-exported
// here so everything that asked the economy before still can.
export { inDisorder, mood, specialistsOf, spareCitizens, specialistOutput, type Mood } from './mood.js';

// ─── the treasury ──────────────────────────────────────────────────────────────

export interface Budget {
  /** Commerce from every city after drag: the gold yield of the tiles they work. */
  commerce: number;
  /** All science this turn: scholars plus researched commerce, raised by any libraries. */
  science: number;
  /** Science from citizens — every city's own people and its scholar specialists — before buildings. */
  scholars: number;
  /** Commerce sent to research by the slider, before buildings. */
  researched: number;
  /** Gold from merchants. `researched + income` is `commerce + specialistGold`. */
  specialistGold: number;
  /** What reaches the treasury: commerce less science. */
  income: number;
  buildingUpkeep: number;
  /** Unit upkeep actually paid: the total less what the cities support for free. */
  unitUpkeep: number;
  /** Upkeep the cities carry for free (the government's `freeUnitsPerCity` each), up to the total. */
  freeUnitUpkeep: number;
  /** income minus both upkeeps. Negative means the treasury is draining. */
  net: number;
}

/**
 * A civilisation's accounts for one turn.
 *
 * Commerce is split city by city rather than as one total, because science buildings
 * will multiply a single city's share (Phase 12) and that cannot be done to a sum.
 * Science rounds down and the remainder goes to gold, so no commerce is ever lost to
 * rounding — at worst a city's odd coin is taxed rather than studied.
 *
 * `rate` overrides the civilisation's own setting, so the question "what would the
 * books look like at 70%?" can be answered without changing anything.
 */
export function budget(state: GameState, content: Content, civId: Id, rate?: number): Budget {
  return budgetAt(budgetBasis(state, content, civId), state.civs[civId]?.researchRate ?? 0, rate);
}

/**
 * Everything in a civilisation's accounts that does not depend on the science rate,
 * worked out once — so trying several rates (as the AI does each turn) costs one pass
 * over the cities rather than one per rate (Phase 15).
 */
export interface BudgetBasis {
  cap: number;
  cities: Array<{ gold: number; own: number; specialistGold: number; percent: number }>;
  buildingUpkeep: number;
  totalUnitUpkeep: number;
  freeUnitUpkeep: number;
}

export function budgetBasis(state: GameState, content: Content, civId: Id): BudgetBasis {
  const cities: BudgetBasis['cities'] = [];
  let buildingUpkeep = 0;
  const civPercent = civEffectTotal(state, content, civId, 'sciencePercent') + traitSum(content, state.civs[civId], 'sciencePercent');
  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.owner !== civId) continue;
    const gold = effectiveYield(state, content, city).gold;
    // A city in disorder produces nothing — its specialists included.
    const specialists = inDisorder(state, content, city) ? { science: 0, gold: 0 } : specialistOutput(state, content, city);
    // Scholar specialists join the city's own science, so its libraries raise them too.
    const own = cityScholars(state, content, city) + specialists.science;
    // Libraries and their successors multiply the city's own science, from both
    // sources — which is why the split is done city by city rather than as one sum.
    const percent = effectTotal(content, city, 'sciencePercent') + civPercent;
    cities.push({ gold, own, specialistGold: specialists.gold, percent });
    for (const building of buildingsOf(content, city)) buildingUpkeep += building.upkeep;
  }
  let totalUnitUpkeep = 0;
  for (const unit of unitsOf(state, civId)) totalUnitUpkeep += content.units.get(unit.type).upkeep;
  // Each city keeps a few units for nothing; only the rest cost gold (ADR-0052).
  const freeUnitUpkeep = Math.min(totalUnitUpkeep, cities.length * governmentEffects(state, content, civId).freeUnitsPerCity);
  return { cap: maxScienceRate(state, content, civId), cities, buildingUpkeep, totalUnitUpkeep, freeUnitUpkeep };
}

/** The accounts at a science rate (`rate`, else the civilisation's own `own` setting). */
export function budgetAt(basis: BudgetBasis, own: number, rate?: number): Budget {
  // The government caps the slider: nothing above its limit is ever studied (ADR-0059).
  const share = Math.max(0, Math.min(basis.cap, rate ?? own));
  let commerce = 0;
  let fromCommerce = 0;
  let scholars = 0;
  let science = 0;
  let specialistGold = 0;
  for (const c of basis.cities) {
    const researched = Math.floor((c.gold * share) / 100);
    commerce += c.gold;
    fromCommerce += researched;
    scholars += c.own;
    specialistGold += c.specialistGold;
    science += Math.floor(((researched + c.own) * (100 + c.percent)) / 100);
  }
  const unitUpkeep = basis.totalUnitUpkeep - basis.freeUnitUpkeep;
  // Merchants pay the treasury directly: their gold is not commerce, so the slider does
  // not split it and distance has already had its say in where they could trade.
  const income = commerce - fromCommerce + specialistGold;
  return {
    commerce,
    science,
    scholars,
    researched: fromCommerce,
    specialistGold,
    income,
    buildingUpkeep: basis.buildingUpkeep,
    unitUpkeep,
    freeUnitUpkeep: basis.freeUnitUpkeep,
    net: income - basis.buildingUpkeep - unitUpkeep,
  };
}

/**
 * Science a city's own people produce, whatever the slider says.
 *
 * A floor under research that grows with the empire rather than its treasury — see the
 * note in rules.json for the measurement behind it. A city in disorder produces none,
 * for the same reason it produces no shields.
 */
export function cityScholars(state: GameState, content: Content, city: City): number {
  if (inDisorder(state, content, city)) return 0;
  return Math.ceil(city.population / content.rules.research.citizensPerScience);
}

/**
 * The cheapest thing a civilisation could sell to stay solvent.
 *
 * Never the palace: selling the seat of government would move the whole empire's
 * administrative centre as a side effect of a bad turn, which is not a consequence
 * anybody would understand.
 */
export function cheapestSellableBuilding(
  state: GameState,
  content: Content,
  civId: Id,
): { city: City; building: number } | null {
  let best: { city: City; building: number; cost: number } | null = null;

  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    if (city.owner !== civId) continue;
    for (const id of city.buildings) {
      const def = content.buildings.get(id);
      if (def.effects.isSeatOfGovernment) continue;
      if (def.upkeep <= 0) continue;
      if (!best || def.cost < best.cost || (def.cost === best.cost && id < best.building)) {
        best = { city, building: id, cost: def.cost };
      }
    }
  }

  return best ? { city: best.city, building: best.building } : null;
}

export function civOf(state: GameState, civId: Id): Civ | null {
  return state.civs[civId] ?? null;
}
